import { api } from './api';

export type EventType = 'event' | 'reminder';
export type EventPriority = 'low' | 'medium' | 'high';
export type EventStatus = 'scheduled' | 'completed' | 'cancelled';
export type AssignmentStatus = 'unseen' | 'seen' | 'completed';
export type NotificationType = 'assigned' | 'updated' | 'deleted' | 'completed' | 'reminder';

export interface EventAssignment {
  id: number;
  event_id: number;
  user_id: number;
  assigned_by: number;
  status: AssignmentStatus;
  seen_at: string | null;
  completed_at: string | null;
  created_at: string;
  user_name: string;
}

export interface EventNotification {
  id: number;
  user_id: number;
  event_id: number;
  type: NotificationType;
  message: string;
  is_read: boolean;
  created_at: string;
  event_title?: string;
  event_start_date?: string;
}

export interface TeamMember {
  id: number;
  name: string;
  email: string;
}

export interface Event {
  id: number;
  user_id: number;
  title: string;
  type: EventType;
  priority: EventPriority;
  start_date: string;
  end_date: string | null;
  all_day: boolean;
  location: string | null;
  description: string | null;
  email_notification_enabled: boolean;
  email_recipients: string[] | null;
  reminder_enabled: boolean;
  reminder_days: number;
  reminder_sent: boolean;
  color: string;
  notes: string | null;
  status: EventStatus;
  created_at: string;
  updated_at: string;
  // Team collaboration fields
  is_owner?: boolean;
  assignment_status?: AssignmentStatus;
  assigned_by_name?: string;
  assignments?: EventAssignment[];
}

export interface CreateEventData {
  title: string;
  type?: EventType;
  priority?: EventPriority;
  start_date: string;
  end_date?: string | null;
  all_day?: boolean;
  location?: string;
  description?: string;
  email_notification_enabled?: boolean;
  email_recipients?: string[];
  reminder_enabled?: boolean;
  reminder_days?: number;
  color?: string;
  notes?: string;
  status?: EventStatus;
  assigned_user_ids?: number[];
}

export interface UpdateEventData {
  title?: string;
  type?: EventType;
  priority?: EventPriority;
  start_date?: string;
  end_date?: string | null;
  all_day?: boolean;
  location?: string;
  description?: string;
  email_notification_enabled?: boolean;
  email_recipients?: string[];
  reminder_enabled?: boolean;
  reminder_days?: number;
  color?: string;
  notes?: string;
  status?: EventStatus;
  assigned_user_ids?: number[];
}

export interface EventFilters {
  status?: EventStatus;
  type?: EventType;
  start_date?: string;
  end_date?: string;
  include_assigned?: boolean;
}

export const eventsApi = {
  /**
   * Get all events for the current user
   */
  getEvents: async (filters?: EventFilters): Promise<Event[]> => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.type) params.append('type', filters.type);
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    if (filters?.include_assigned) params.append('include_assigned', 'true');

    const query = params.toString();
    const response = await api.get(`/events${query ? `?${query}` : ''}`);
    return response.data.events;
  },

  /**
   * Get a single event by ID
   */
  getEvent: async (eventId: number): Promise<Event> => {
    const response = await api.get(`/events/${eventId}`);
    return response.data.event;
  },

  /**
   * Create a new event
   */
  createEvent: async (data: CreateEventData): Promise<Event> => {
    const response = await api.post('/events', data);
    return response.data.event;
  },

  /**
   * Update an existing event
   */
  updateEvent: async (eventId: number, data: UpdateEventData): Promise<Event> => {
    const response = await api.put(`/events/${eventId}`, data);
    return response.data.event;
  },

  /**
   * Delete an event
   */
  deleteEvent: async (eventId: number): Promise<void> => {
    await api.delete(`/events/${eventId}`);
  },

  /**
   * Get count of upcoming events
   */
  getUpcomingCount: async (): Promise<number> => {
    const response = await api.get('/events/count/upcoming');
    return response.data.count;
  },

  /**
   * Mark an event as seen (for assigned users)
   */
  markAsSeen: async (eventId: number): Promise<void> => {
    await api.post(`/events/${eventId}/seen`);
  },

  /**
   * Mark an event as completed (for assigned users)
   */
  markAsCompleted: async (eventId: number): Promise<void> => {
    await api.post(`/events/${eventId}/completed`);
  },

  /**
   * Get unread count (unseen assignments + unread notifications)
   */
  getUnreadCount: async (): Promise<{ unseen_assignments: number; unread_notifications: number; total: number }> => {
    const response = await api.get('/events/unread/count');
    return response.data;
  },

  /**
   * Get notifications
   */
  getNotifications: async (): Promise<EventNotification[]> => {
    const response = await api.get('/events/notifications/list');
    return response.data.notifications;
  },

  /**
   * Mark notification as read
   */
  markNotificationRead: async (notificationId: number): Promise<void> => {
    await api.post(`/events/notifications/${notificationId}/read`);
  },

  /**
   * Get team members for assignment
   */
  getTeamMembers: async (): Promise<TeamMember[]> => {
    const response = await api.get('/events/team/members');
    return response.data.members;
  },
};

export default eventsApi;
